import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router";

import {
	HookFormCheckbox,
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectAsyncFormGroup,
	ScheduleAView,
	TextWithLoader,
} from "components";
import { FormColumn, FormRow } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { getEntityTypeName, partnerTypes } from "mappers";
import { dropdownValuesService, scheduleAListService } from "services";
import {
	PartnerTypes,
	ScheduleAValueType,
	SplitInformationParamsProps,
	SplitInformationProps,
} from "types";
import { floatWithComma2Normalizer } from "validators";
import { maxValue100, onlyPercent, required } from "validators/validation";

import ToggleScheduleAButton from "./toggleScheduleAButton";
import { WaterfallExampleMessage } from "./waterfallExampleMessage";
import { WaterfallWarningMessage } from "./waterfallWarningMessage";

import styles from "./../splitProfile.module.scss";

export const SecondarySplitInformationFields = ({
	name,
	isLoading = false,
	splitInfo,
	setToggleAgentSelect,
	toggleAgentSelect,
	isStraightSplit,
	isWaterfall,
}: SplitInformationProps) => {
	const params = useParams<SplitInformationParamsProps>();
	const {
		setValue,
		watch,
		register,
		getValues,

		formState: { errors },
	} = useFormContext();
	const [scheduleAOption, setScheduleAOption] = useState<ScheduleAValueType>({
		id: "",
		name: "",
	});
	const [showScheduleAData, setShowScheduleAData] = useState<boolean>(false);

	useEffect(() => {
		if (splitInfo?.agent) {
			let agent = {
				id: splitInfo.agent.id,
				firstName: splitInfo.agent.firstName,
				lastName: splitInfo.agent.lastName,
				name: splitInfo.agent.name,
				entityType: splitInfo.agent.entityType,
				partnerType: splitInfo.agent.partnerType,
				partnerTypeDisplayName: splitInfo.agent.partnerTypeDisplayName,
			};
			setValue(`${name}.agent`, agent);
		}
	}, [splitInfo?.agentId, splitInfo?.agent, name, setValue]);

	const agent = watch(`${name}.agent`);
	const totalRevenueShare = watch("totalRevenueShare");
	const scheduleAFormState = getValues(`${name}.schedule_a_id`);

	const isAgentSelected = isEmpty(agent);

	useEffect(() => {
		setValue(`${name}.schedule_a_id`, null);
	}, [agent?.id, setValue, name]);

	const loadAgents = useCallback(
		async (search, prevOptions, { page }) => {
			let agents = await dropdownValuesService.getSplitProfileAgents(
				params.agentId,
				search,
				page,
				true,
			);
			const hasMore =
				prevOptions.length + agents.items.length < agents.count;

			return {
				options: agents.items.map(a => ({
					id: a.id,
					firstName: a.first_name,
					lastName: a.last_name,
					name: `${a.name} ${a.is_active ? "" : "(inactive)"}`,
					entityType: a.entity_type,
					partnerType: a.partner_type,
					partnerTypeDisplayName: a.partner_type_display_name,
				})),
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		},
		[params.agentId],
	);

	const loadScheduleA = useCallback(
		async (prevOptions, { page }) => {
			if (!agent?.id) {
				return {
					options: [],
					hasMore: false,
					additional: {
						page: page + 1,
					},
				};
			}

			const scheduleAValue = await scheduleAListService.getScheduleAList({
				userId: agent?.id,
				page,
			});
			const hasMore =
				prevOptions.length + scheduleAValue.items.length <
				scheduleAValue.count;
			if (scheduleAValue.items.length === 1) {
				setScheduleAOption(scheduleAValue.items[0]);
			}

			if (splitInfo?.schedule_a_id) {
				const scheduleAOptions = scheduleAValue?.items?.find(
					x => x.id === splitInfo?.schedule_a_id,
				);
				setValue(`${name}.schedule_a_id`, scheduleAOptions);
				setScheduleAOption({
					id: scheduleAOptions?.id,
					name: scheduleAOptions?.name,
				});
			}

			return {
				options: scheduleAValue.items.map(a => ({
					id: a.id,
					name: a.name,
				})),
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		},
		[agent?.id, name, setValue, splitInfo?.schedule_a_id],
	);

	useEffect(() => {
		if (!isEmpty(scheduleAOption)) {
			setValue(`${name}.schedule_a_id`, {
				id: scheduleAOption?.id,
				name: scheduleAOption?.name,
			});
		}
	}, [name, scheduleAOption, setValue]);

	const isScheduleForCalculations = watch(
		`${name}.isUseSecondaryAgentScheduleForCalculations`,
	);

	const isUsedForCalculations = isScheduleForCalculations === "true";
	const isShowScheduleAIconStyles =
		isUsedForCalculations && !isEmpty(scheduleAFormState?.id)
			? styles.containerSelectClassName
			: null;
	const scheduleAColumnStyles =
		isUsedForCalculations && !isEmpty(scheduleAFormState?.id)
			? "d-flex justify-content-between"
			: null;
	const scheduleAContainerStyles = !isEmpty(scheduleAFormState?.id)
		? styles.scheduleAContainer
		: null;

	useEffect(() => {
		if (!isUsedForCalculations) {
			setValue(`${name}.schedule_a_id`, "");
			setShowScheduleAData(false);
		}
	}, [isUsedForCalculations, name, setValue]);

	useEffect(() => {
		if (agent?.id !== splitInfo?.agent?.id) {
			let defaultPrivateValue =
				agent?.partnerType === partnerTypes.BUSINESS_DEVELOPMENT ||
				agent?.partnerType === partnerTypes.INSIDE_SALES_MANAGER ||
				agent?.partnerType === partnerTypes.OUTSIDE_SALES_MANAGER;

			setValue(`${name}.isPrivate`, defaultPrivateValue);
		}
	}, [agent?.id, agent?.partnerType, name, setValue, splitInfo?.agent?.id]);

	const handleAgentSelect = value => {
		if (value) {
			setToggleAgentSelect?.(!toggleAgentSelect);
		} else return;
	};

	const payoutAmountBeforeAdjustmentsValue = getValues(
		`${name}.calculation.payoutAmountBeforeAdjustments`,
	);
	const isReadOnlySplitPercentValue = getValues(
		`${name}.isReadOnlySplitPercent`,
	);
	const isWaterfallSplitPercentWarningValue = getValues(
		`${name}.isWaterfallSplitPercentWarning`,
	);

	const exampleValue = floatWithComma2Normalizer(
		payoutAmountBeforeAdjustmentsValue,
	);

	return (
		<>
			<FormRow>
				<FormColumn>
					<HookFormCheckbox
						register={register}
						name={`${name}.isPrivate`}
						id={`${name}.isPrivate`}
						style={{ paddingBottom: "1.25rem" }}
						label="Private"
						blurLoading={isLoading}
						blurWidth="200px"
					/>
				</FormColumn>
				<FormColumn></FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn data-autotest="agent">
					<HookSelectAsyncFormGroup
						name={`${name}.agent`}
						label="Agent"
						loadOptions={loadAgents}
						isSearchable={true}
						getOptionLabel={option => option.name}
						getOptionValue={option => option.id}
						placeholder="Please Select"
						cacheOptions
						defaultOptions
						onChange={handleAgentSelect}
						defaultValue={splitInfo?.agent}
						debounceTimeout={DebounceTimeout}
						additional={{ page: 0 }}
						rules={{
							validate: {
								required,
							},
						}}
						blurLoading={isLoading}
						showRequiredMark
					/>
				</FormColumn>
				<FormColumn data-autotest="residualsSplit">
					<div className={isStraightSplit ? "d-block" : "d-none"}>
						<TextWithLoader
							label="Residuals Split, %"
							value={totalRevenueShare}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					</div>
					<HookFormInputGroup
						register={register}
						name={`${name}.splitPercent`}
						label="Residuals Split, %"
						placeholder="0"
						colLabel={6}
						colInput={6}
						validator={{
							required,
							maxValue100,
						}}
						normalize={onlyPercent}
						errors={errors}
						blurLoading={isLoading}
						showRequiredMark={!isStraightSplit}
						defaultValue={splitInfo?.splitPercent}
						readOnly={isStraightSplit}
						isPlainText={isReadOnlySplitPercentValue}
						className={isStraightSplit ? "d-none" : "d-flex"}
						external={
							isWaterfall && (
								<div>
									{!isReadOnlySplitPercentValue && (
										<div>
											<WaterfallExampleMessage
												value={exampleValue}
											/>
										</div>
									)}

									{isWaterfallSplitPercentWarningValue && (
										<WaterfallWarningMessage />
									)}
								</div>
							)
						}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn data-autotest="isUseSecondaryAgentScheduleForCalculations">
					<div className="form-group row">
						<Form.Label
							htmlFor="isUseSecondaryAgentScheduleForCalculations"
							className="form-label col-form-label col-sm-6"
						>
							Use Secondary Agent Schedule for Calculations
						</Form.Label>
						<div className="col-sm-6 d-flex align-items-center">
							<div className={styles.wrappRadio}>
								<HookFormRadioButton
									label="Yes"
									value="true"
									register={register}
									name={`${name}.isUseSecondaryAgentScheduleForCalculations`}
									className={styles.radioButton}
									id={`${name}.isUseSecondaryAgentScheduleForCalculations`}
									blurLoading={isLoading}
									defaultChecked={
										!isEmpty(splitInfo?.schedule_a_id)
									}
								/>
								<HookFormRadioButton
									label="No"
									value="false"
									register={register}
									name={`${name}.isUseSecondaryAgentScheduleForCalculations`}
									className={styles.radioButton}
									id={`${name}.isNotUseSecondaryAgentScheduleForCalculations`}
									blurLoading={isLoading}
									defaultChecked={isEmpty(
										splitInfo?.schedule_a_id,
									)}
								/>
							</div>
						</div>
					</div>
				</FormColumn>
				{isUsedForCalculations && (
					<FormColumn
						data-autotest="schedule_a_id"
						className={clsx(scheduleAColumnStyles)}
					>
						<HookSelectAsyncFormGroup
							// Fix reloading options after agent changes
							key={JSON.stringify(agent)}
							name={`${name}.schedule_a_id`}
							label="Schedule A"
							loadOptions={loadScheduleA}
							isSearchable={false}
							defaultValue={
								isEmpty(scheduleAOption.id)
									? !isEmpty(splitInfo?.schedule_a_id)
										? scheduleAFormState
										: null
									: scheduleAOption
							}
							getOptionLabel={option => option.name}
							getOptionValue={option => option.id}
							placeholder="Please Select"
							cacheOptions
							defaultOptions
							debounceTimeout={DebounceTimeout}
							additional={{ page: 0 }}
							blurLoading={isLoading}
							containerClassName={clsx(
								scheduleAContainerStyles,
								"mt-2",
							)}
							containerSelectClassName={clsx(
								isShowScheduleAIconStyles,
							)}
						/>
						<ToggleScheduleAButton
							isUsedForCalculations={isUsedForCalculations}
							scheduleAFormState={scheduleAFormState}
							showScheduleAData={showScheduleAData}
							setShowScheduleAData={setShowScheduleAData}
						/>
					</FormColumn>
				)}
			</FormRow>
			<FormRow className="mt-3">
				<FormColumn data-autotest="entityType">
					{!isAgentSelected && (
						<TextWithLoader
							label="Entity Type"
							value={getEntityTypeName(agent?.entityType)}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					)}
				</FormColumn>
			</FormRow>
			<FormRow className="mt-3">
				<FormColumn data-autotest="partnerType">
					{!isAgentSelected && (
						<TextWithLoader
							label="Partner Type"
							value={
								PartnerTypes.getByValue(agent?.partnerType)
									?.displayName || ""
							}
							loading={isLoading}
							labelCol={6}
							valueCol={6}
						/>
					)}
				</FormColumn>
			</FormRow>
			{showScheduleAData && scheduleAFormState?.id && (
				<ScheduleAView
					userId={agent?.id}
					scheduleId={scheduleAFormState?.id}
					scheduleAName={scheduleAFormState?.name}
				/>
			)}
		</>
	);
};
